import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import LoadingBlock from "../LoadingBlock/LoadingBlock";
import { baseChartOptions } from "./charts.config";

import exportingModule from "highcharts/modules/exporting";
import offlineExportingModule from "highcharts/modules/offline-exporting";
import exportDataModule from "highcharts/modules/export-data";

function initModule(mod, H) {
  const fn = mod?.default || mod;
  if (typeof fn === "function") fn(H);
}

initModule(exportingModule, Highcharts);
initModule(offlineExportingModule, Highcharts);
initModule(exportDataModule, Highcharts);

function toSeriesNumbers(arr) {
  return (arr || []).map((v) => (Number.isFinite(+v) ? +v : 0));
}

function buildQualitativeSeries(name, sourceData, color, exportMode, exportYOffset) {
  return {
    name,
    data: toSeriesNumbers(sourceData),
    color,
    dataLabels: {
      y: exportMode ? exportYOffset : -10,
    },
  };
}

export default function QualitativePerDayLineChart({
  title = "Análise qualitativa por dia",
  categories = [],
  positive = [],
  neutral = [],
  negative = [],
  loading = false,
  filename = "qualitativo-por-dia",
  loadingLabel = "Carregando qualitativo por dia...",
  exportMode = false,
}) {
  const chartRef = useRef(null);
  const TEXT = "var(--text-color)";

  const sum = (arr) =>
    Array.isArray(arr) ? arr.reduce((acc, v) => acc + (Number(v) || 0), 0) : 0;

  const positiveSum = sum(positive);
  const neutralSum = sum(neutral);
  const negativeSum = sum(negative);
  const positiveData = useMemo(() => toSeriesNumbers(positive), [positive]);
  const neutralData = useMemo(() => toSeriesNumbers(neutral), [neutral]);
  const negativeData = useMemo(() => toSeriesNumbers(negative), [negative]);
  const topPointByIndex = useMemo(() => {
    const maxLength = Math.max(positiveData.length, neutralData.length, negativeData.length);

    return Array.from({ length: maxLength }, (_, index) => {
      const seriesValues = [
        { name: "Neutra", value: Number(neutralData[index]) || 0 },
        { name: "Negativa", value: Number(negativeData[index]) || 0 },
        { name: "Positiva", value: Number(positiveData[index]) || 0 },
      ];

      return seriesValues.reduce(
        (best, current) => (current.value > best.value ? current : best),
        { name: null, value: 0 }
      );
    });
  }, [positiveData, neutralData, negativeData]);

  const CARD_MIN_H = 360;
  const CHART_H = 320;
  const EXPORT_CHART_H = 620;
  const EXPORT_DATA_LABEL_FONT_SIZE =
    categories.length >= 20 ? "15px" : categories.length >= 12 ? "17px" : "19px";
  const EXPORT_DATA_LABEL_PADDING = categories.length >= 20 ? 4 : 6;

  const [chartKey, setChartKey] = useState(0);
  useEffect(() => {
    const onEnd = () => setChartKey((k) => k + 1);
    window.addEventListener("dashboard-export:end", onEnd);
    return () => window.removeEventListener("dashboard-export:end", onEnd);
  }, []);

  const wrapStyle = useMemo(() => {
    if (!exportMode) {
      return {
        position: "relative",
        borderRadius: 12,
        padding: 12,
        border: "none",
        background: "var(--background-graphic)",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
        overflow: "hidden",
        minHeight: CARD_MIN_H,
      };
    }

    return {
      position: "relative",
      borderRadius: 0,
      padding: 0,
      border: "none",
      background: "var(--background-graphic)",
      boxShadow: "none",
      overflow: "hidden",
      minHeight: 560,
      width: "100%",
    };
  }, [exportMode]);

  const options = useMemo(() => {
    return {
      ...baseChartOptions,

      chart: {
        type: "line",
        height: exportMode ? EXPORT_CHART_H : CHART_H,
        spacing: exportMode ? [0, 0, 0, 0] : [24, 16, 16, 24],
        backgroundColor: "transparent",
        animation: !exportMode,
        marginTop: exportMode ? 0 : 64,
      },

      title: {
        text: exportMode ? null : title,
        align: "left",
        x: 0,
        y: -3,
        margin: 12,
        style: { fontSize: "14px", fontWeight: "800", color: "#614a9e" },
      },

      exporting: { enabled: false },

      xAxis: {
        categories,
        tickLength: 0,
        lineWidth: 0,
        labels: {
          style: {
            fontSize: exportMode ? "13px" : "11px",
            fontWeight: "700",
            color: TEXT,
          },
        },
      },

      yAxis: {
        title: { text: null },
        min: 0,
        allowDecimals: false,
        gridLineDashStyle: "ShortDot",
        gridLineColor: "rgba(17, 24, 39, 0.10)",
        labels: {
          style: {
            fontSize: exportMode ? "17px" : "11px",
            fontWeight: "700",
            color: TEXT,
          },
        },
      },

      legend: exportMode
        ? { enabled: false }
        : {
            enabled: true,
            align: "center",
            verticalAlign: "bottom",
            symbolRadius: 6,
            itemStyle: {
              fontWeight: "700",
              fontSize: "11px",
              color: TEXT,
            },
            labelFormatter: function () {
              const map = {
                Positiva: positiveSum,
                Neutra: neutralSum,
                Negativa: negativeSum,
              };
              return `${this.name} (${map[this.name] ?? 0})`;
            },
          },

      tooltip: {
        shared: true,
        useHTML: true,
        valueSuffix: " notícias",
      },

      plotOptions: {
        series: {
          lineWidth: exportMode ? 4 : 3,
          marker: { enabled: true, radius: exportMode ? 5 : 4, symbol: "circle" },
          dataLabels: {
            enabled: true,
            allowOverlap: exportMode,
            crop: false,
            overflow: "allow",
            backgroundColor: exportMode ? "rgba(255, 255, 255, 0.92)" : "transparent",
            borderRadius: exportMode ? 4 : 0,
            padding: exportMode ? EXPORT_DATA_LABEL_PADDING : 0,
            style: {
              fontWeight: "900",
              fontSize: exportMode ? EXPORT_DATA_LABEL_FONT_SIZE : "11px",
              color: TEXT,
              textOutline: "none",
            },
            formatter: function () {
              const value = Number(this.y) || 0;

              if (!exportMode) {
                const pointIndex = this.point?.index ?? -1;
                const topPoint = topPointByIndex[pointIndex] || { name: null, value: 0 };

                if (
                  value === 0 ||
                  value !== topPoint.value ||
                  this.series.name !== topPoint.name
                ) {
                  return null;
                }
              }

              return value;
            },
          },
        },
      },

      series: [
        buildQualitativeSeries("Neutra", neutralData, "#F59E0B", exportMode, -22),
        buildQualitativeSeries("Negativa", negativeData, "#EF4444", exportMode, 20),
        buildQualitativeSeries("Positiva", positiveData, "#16A34A", exportMode, 4),
      ],

      credits: { enabled: false },
    };
  }, [
    title,
    categories,
    positiveSum,
    neutralSum,
    negativeSum,
    exportMode,
    EXPORT_CHART_H,
    EXPORT_DATA_LABEL_FONT_SIZE,
    EXPORT_DATA_LABEL_PADDING,
    positiveData,
    neutralData,
    negativeData,
    topPointByIndex,
  ]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    const t = setTimeout(() => {
      try {
        chart.reflow();

        if (exportMode) {
          const w = chart.renderTo?.clientWidth || null;
          chart.setSize(w, EXPORT_CHART_H, false);
          chart.reflow();
        }
      } catch {}
    }, exportMode ? 60 : 0);

    return () => clearTimeout(t);
  }, [exportMode, categories, positive, neutral, negative, EXPORT_CHART_H]);

  const handleExportPNG = () => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    chart.exportChart(
      { type: "image/png", filename },
      { chart: { backgroundColor: "var(--background-graphic)" } }
    );
  };

  const exportBtnStyle = {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 5,

    height: 34,
    padding: "0 12px",
    borderRadius: 12,
    border: "none",
    background: "var(--bottom-dropdown-color)",
    color: "var(--text-color)",
    fontWeight: 900,
    fontSize: 12,

    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    userSelect: "none",
    transition: "transform .08s ease",
  };

  const exportBtnHoverStyle = {
    boxShadow: "0 6px 14px rgba(97, 74, 158, 0.15)",
    backgroundColor: "#614a9e",
    color: "white",
  };

  const iconStyle = {
    width: 18,
    height: 18,
    display: "grid",
    placeItems: "center",
    borderRadius: 8,
    background:
      "linear-gradient(135deg, var(--icon-gradient-start), var(--icon-gradient-end))",
    color: "var(--color-text-button-action)",
    fontSize: 12,
    lineHeight: 1,
    flex: "0 0 auto",
  };

  return (
    <LoadingBlock loading={loading} label={loadingLabel}>
      <div style={wrapStyle}>
        {!exportMode ? (
          <ExportButton
            onClick={handleExportPNG}
            style={exportBtnStyle}
            hoverStyle={exportBtnHoverStyle}
            iconStyle={iconStyle}
          />
        ) : null}

        <HighchartsReact
          key={chartKey}
          ref={chartRef}
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: { height: exportMode ? EXPORT_CHART_H : CHART_H } }}
        />
      </div>
    </LoadingBlock>
  );
}

function ExportButton({ onClick, style, hoverStyle, iconStyle }) {
  const [hover, setHover] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...style, ...(hover ? hoverStyle : {}) }}
      title="Exportar PNG"
    >
      <span style={iconStyle}>⭳</span>
      Exportar PNG
    </button>
  );
}
